import { Scene, Mesh } from 'three';

import Sizes from './Utils/Sizes.js';
import Time from './Utils/Time.js';
import Resources from './Utils/Resources.js';
import Debug from './Utils/Debug.js';
import Camera from './Camera.js';
import Renderer from './Renderer.js';
import World from './World/World.js';

import resources from './resources.js';
import PointerCoordinates from './Utils/PointerCoordinates.js';
import EventEmitter from './Utils/EventEmitter.js';
import Events from './Utils/Events.js';
import SequentialWaypointControls from './Controls/SequentialWaypointControls.js';
import Menu from './Interface/Menu.js';
import FadeOverlay from './Interface/FadeOverlay.js';
import ItemDetailUI from './Interface/ItemDetailUI.js';

let instance = null;

export default class Experience {
    constructor(canvas) {
        // Singleton
        // eslint-disable-next-line no-constructor-return
        if (instance) return instance;

        instance = this;

        // Setup
        this.canvas = canvas;
        this.eventEmitter = new EventEmitter();
        this.debug = new Debug();
        this.sizes = new Sizes();
        this.pointer = new PointerCoordinates(this.canvas);
        this.time = new Time();
        this.scene = new Scene();
        this.resources = new Resources(resources);
        this.camera = new Camera();
        this.renderer = new Renderer();
        this.menu = new Menu(this.canvas);
        this.itemDetailUI = new ItemDetailUI(this.menu);
        this.overlay = new FadeOverlay(this.scene, this.camera.instance);
        this.controls = new SequentialWaypointControls(
            this.camera.instance,
            this.canvas,
            this.time,
            this.debug,
            this.overlay
        );
        this.world = new World();

        // Resize event
        this.eventEmitter.on(Events.RESIZE, this, () => {
            this.resize();
        });

        // Time tick event
        this.eventEmitter.on(Events.TICK, this, () => {
            this.update();
        });
    }

    resize() {
        this.camera.resize();
        this.renderer.resize();
    }

    update() {
        this.renderer.update();
        this.controls.update();
        this.debug.update();
    }

    destroy() {
        // Remove the listeners.
        this.eventEmitter.off(Events.RESIZE, this);
        this.eventEmitter.off(Events.TICK, this);
        this.world.destroy();
        this.menu.destroy();
        this.itemDetailUI.destroy();

        // You can remove the event listeners present in the window too, like the one we set in the Sizes class.

        // Destroy everything you do not need in the scene.
        this.scene.traverse((child) => {
            if (child instanceof Mesh) {
                child.geometry.dispose();
                Object.values(child.material).forEach((material) => {
                    // Understand if the value exists and has a dispose function.
                    if (material && typeof material.dispose === 'function') {
                        material.dispose();
                    }
                });
            }
        });

        this.controls.destroy();
        this.renderer.instance.dispose();

        if (this.debug.active) this.debug.ui.destroy();

        this.canvas.remove();
    }
}
